import { path } from 'ramda'
import { Query } from 'mingo'

function IsDisabled(condition, data) {
  if (!condition) return false
  return condition === true ? condition : new Query(condition).test(data)
}

function IsDefined(value, defaultValue = true) {
  if (value || typeof value === 'boolean') return value
  return defaultValue
}

function CurlyTemplateReplacer(str, data) {
  let params = str.match(/\{(.*?)\}/gi) || []
  return params.reduce((accumulator, current) => {
    let key = current.replace(/{|}/g, '')
    return accumulator.replace(current, path(key.split('.'), data))
  }, str)
}

function ValidateCondition(condition, schema = { component: '' }, data) {
  const { component, settings } = schema

  const isDynamic = isDynamicComponent(component)

  if (!condition) return true
  if (!isDynamic) return new Query(condition).test(data)

  if ('*' in condition) return new Query(condition['*']).test(data)



  //check dynamic componets that has a list as parent
  if (isDynamic && schema.parentIsList && !schema.isSettings) {
    return new Query(condition).test(data)
  }

  // Less conditions than components -> let each field do the check seperatly
  if (
    (typeof component === 'string' ? component.split(',').length : component.length) >
    Object.keys(condition).length
  )
    return true

  if (settings) return true
  // If all conditions fail -> hide
  return Object.values(condition).some(c => new Query(c).test(data))
}

function Filter(value, options = {}) {
  let { keys = [], placement = [] } = options
  let pattern = new RegExp('^/(.+)/$', 'gi')

  if (!(keys instanceof Array)) keys = [keys]
  if (!(placement instanceof Array)) placement = [placement]

  const Filters = {
    keys: field => {
      if (!keys.length) return true
      return keys.includes(field.key)
    },
    placement: field => {
      if (!placement.length) return true
      return placement.some(key => {
        if (!field.placement) return
        if (key && key.match(pattern)) return field.placement.match(pattern.exec(key)[1])
        const match = field.placement instanceof Array ? field.placement : [field.placement]
        return match.includes(key)
      })
    },
  }

  return value.filter(field => Object.values(Filters).every(f => f(field)))
}

const Clone = function () {
  return JSON.parse(JSON.stringify.apply(this, arguments))
}

function GetCustomFieldsRules(value, filter) {

  if (filter) value = Filter(value, filter)
  let fieldsWithValidation = value.filter(c => path(['options', 'validation'], c))
  return fieldsWithValidation.reduce((res, cur) => {
    res[`customFields.${cur.key}`] = cur.options.validation
    return res
  }, {})
}

function ValueComp() {

  return {
    get() {
      return this.value
    },
    set(val) {
      this.$emit('input', val)
    },
  }
}

function ExtractString(str, pattern, options = {}) {
  let { group = 1 } = options
  let match = str.match(pattern)
  if (!match) return

  return match[group]
}

const BracketsRegex = /^\[.+\](:(\d+(fr|px|%)))?$/i
function RemoveBrackets(val) {

  if (!val.match(BracketsRegex)) return val
  return val.replace(/^\[/g, '').replace(/\](:\d+(fr|px|%))?$/g, '')
}

/*
  Hentet fra
  https://gist.github.com/softwarespot/76252a838efdcace2df1f9c724e37351
*/
function Evaluate(code, args = {}) {

  return function evaluateEval() {
    const argsStr = Object.keys(args)
      .map(key => `${key} = this.${key}`)
      .join(',')
    const argsDef = argsStr ? `let ${argsStr};` : ''

    return eval(`${argsDef}${code}`)
  }.call(args)
}

function Debounce(func, wait, immediate) {
  let timeout
  return function () {
    let context = this,
      args = arguments
    let later = function () {
      timeout = null
      if (!immediate) func.apply(context, args)
    }
    let callNow = immediate && !timeout
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
    if (callNow) func.apply(context, args)
  }
}

function CopyToClipboard(str) {
  const el = document.createElement('textarea')
  el.value = str
  document.body.appendChild(el)
  el.select()
  document.execCommand('copy')
  document.body.removeChild(el)
}

function isList(value) {

  if (typeof value === 'string') return Boolean(value.match(BracketsRegex))
  return (
    value.isList || (typeof value.component === 'string' && value.component.match(BracketsRegex))
  )
}

function getDynamicComponentTemplate(schema) {
  if (typeof schema.subcomponents === 'string') {
    let components = schema.subcomponents.split(',')
    if (components.length === 1) return undefined
    return Object.fromEntries(components.map(e => [e.split(':')[1], null]))
  }

  if (schema.subcomponents instanceof Array) {
    return Object.fromEntries(schema.subcomponents.map(({ key }) => [key, null]))
  }

  return {}
}

function isDynamicComponent(value) {
  if (value instanceof Array) return true
  if (typeof value === 'string') return Boolean(value.match(/.+:.+:.+/))
  return false
}

function DynamicComponentToString(value) {
  const wrapList = (v, isList) => (isList ? `[${v}]` : v)
  const addItemMinWidth = (v, w) => (w ? `${v}:${w}` : v)
  const createEntry = ({ component, key, label, isList, itemMinWidth }) => {
    return addItemMinWidth(wrapList(`${component}:${key}:${label}`, isList), itemMinWidth)
  }

  return addItemMinWidth(
    wrapList(value.components.map(createEntry).join(','), value.isList),
    value.itemMinWidth,
  )
}
function DynamicComponentToObject(value) {
  if (typeof value !== 'string') return value

  const components = RemoveBrackets(value)
    .split(',')
    .map(entry => {
      const [component, key, label] = RemoveBrackets(entry).split(':')
      return {
        component,
        key,
        label,
        isList: isList(entry),
        itemMinWidth: ExtractString(entry, BracketsRegex, { group: 2 }),
      }
    })

  return {
    components,
    isList: isList(value),
    itemMinWidth: ExtractString(value, BracketsRegex, { group: 2 }),
  }
}

function LoadScript(src, options = { type: 'text/javascript' }) {
  let { type } = options
  if (document.querySelector(`script[src="${src}"]`)) return

  let script = document.createElement('script')
  script.type = type
  script.src = src

  document.body.appendChild(script)
}

function Wait(ms) {
  return new Promise((resolve, reject) => {
    setTimeout(resolve, ms)
  })
}

async function WaitUntil(func, options = {}) {
  let { interval = 200, limit = 50 } = options
  let count = 0
  let result = false

  while (count < limit) {
    count++
    if (func()) break
    await Wait(interval)
  }

  if (count >= limit) throw new Error('Could not resolve')
  return true
}

function SortItems(items) {
  return items
    .reduce(
      (result, field) => {
        if (path(['options', 'location'], field)) {
          const [block, placement] = field.options.location.split(':')

          const blockIndex = result.findIndex(i => i.key === block)

          if (~blockIndex) {
            const index = result.findIndex(i => i.key === field.key)
            result.splice(index, 1) // delete item

            let newIndex = blockIndex
            if (placement === 'after') {
              newIndex = blockIndex + (blockIndex < index ? 1 : 0)
            } else if (placement === 'before') {
              newIndex = blockIndex + (blockIndex < index ? 0 : -1)
              if (newIndex < 0) newIndex = 0
            }

            // Insert field at new position
            result.splice(newIndex, 0, field)
          }
        }

        return result
      },
      [...items],
    )

}

export {
  CopyToClipboard,
  Filter,
  ValueComp,
  CurlyTemplateReplacer,
  Clone,
  GetCustomFieldsRules,
  RemoveBrackets,
  BracketsRegex,
  isList,
  ExtractString,
  Evaluate,
  Debounce,
  DynamicComponentToString,
  DynamicComponentToObject,
  getDynamicComponentTemplate,
  ValidateCondition,
  IsDisabled,
  LoadScript,
  IsDefined,
  WaitUntil,
  isDynamicComponent,
  SortItems
}
